// On-demand sync (SPEC §6.3): fetch what the user is looking at from Sertica into the store, then
// reload it. The API reuses a job finished in the last minute, and merges a request with the
// same open one, so a repeat (or StrictMode's double effect) costs no extra Sertica reads.
import { useEffect, useRef, useState } from "react";
import type { SyncScope } from "@po/shared";
import { api, type Job, type JobProgress } from "./api";

export type ViewSyncBody =
  | { kind: "search"; q: string; table: "requisitions" | "purchase-orders" | "rfqs" }
  | { kind: "rfq-page"; nos: string[] }
  | { kind: "page"; table: "requisitions" | "purchase-orders"; nos: string[] }
  | { kind: "item-search"; q: string }
  | { kind: "item-group"; no: string }
  | { kind: "user-rights" }
  /** A page being opened: its part, when the server finds it older than 5 min ("fresh" otherwise). */
  | { kind: "tab"; scope: Exclude<SyncScope, "all" | "full"> };
export type ViewSyncState =
  | { status: "idle" }
  | { status: "syncing"; kind: ViewSyncBody["kind"]; progress: JobProgress | null }
  | { status: "done"; at: Date }
  | { status: "failed" }
  | { status: "unavailable" };

/** Runs once each time `body` changes (compared as JSON); `null` does nothing. */
export function useViewSync(body: ViewSyncBody | null, onDone: () => void): ViewSyncState {
  const [state, setState] = useState<ViewSyncState>({ status: "idle" });
  const key = body ? JSON.stringify(body) : null;
  const latest = useRef(key);
  latest.current = key;
  useEffect(() => {
    if (!body || !key) return setState({ status: "idle" });
    // A result for a request the user has moved on from is dropped.
    const set = (s: ViewSyncState) => latest.current === key && setState(s);
    void (async () => {
      // A page being opened shows nothing until the server says there is something to fetch.
      if (body.kind !== "tab") set({ status: "syncing", kind: body.kind, progress: null });
      let job: Job;
      try {
        job = await api<Job>("/sync/view", { method: "POST", body: JSON.stringify(body) });
      } catch {
        return set({ status: "failed" });
      }
      // Synced recently enough: nothing to fetch, nothing to reload.
      if (job.state === "fresh") return set({ status: "idle" });
      const started = Date.now();
      for (;;) {
        if (job.state === "done") {
          if (latest.current === key) onDone();
          return set({ status: "done", at: job.finishedAt ? new Date(job.finishedAt) : new Date() });
        }
        // A page's whole part (a delta, a budget run) takes longer than one search or record.
        if (job.state === "failed" || Date.now() - started > (body.kind === "tab" ? 300_000 : 60_000)) return set({ status: "failed" });
        set({ status: "syncing", kind: body.kind, progress: job.progress ?? null });
        // Nobody claimed it: the worker is not running.
        if (job.state === "queued" && Date.now() - started > 10_000) return set({ status: "unavailable" });
        await new Promise((r) => setTimeout(r, 1000));
        if (latest.current !== key) return;
        try {
          job = await api<Job>(`/refresh/${job.id}`, { background: true });
        } catch {
          // a dropped poll: try again on the next tick
        }
      }
    })();
  }, [key]);
  return state;
}
